import {
  Entity,
  RELATION_CONSUMES_API,
  RELATION_DEPENDS_ON,
  RELATION_PROVIDES_API,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';

// Категория узла определяет цвет и фильтр: внешние сервисы отделены от инфраструктурных ресурсов.
export type NodeCategory = 'component' | 'api' | 'resource' | 'external';

export type EdgeCategory = 'dependsOn' | 'providesApi' | 'consumesApi';

export type ArchNode = {
  id: string; // entity ref, например component:default/signaling-api
  entity: Entity;
  category: NodeCategory;
  label: string;
  subtitle: string;
  groupId: string;
};

export type ArchEdge = {
  id: string;
  source: string;
  target: string;
  category: EdgeCategory;
};

export type ArchGroup = {
  id: string;
  label: string;
  entity?: Entity; // System; у служебных групп сущности нет
};

export type ArchGraph = {
  nodes: ArchNode[];
  edges: ArchEdge[];
  groups: ArchGroup[];
};

export const SHARED_GROUP_ID = 'group:shared';
const SHARED_GROUP_LABEL = 'Общие ресурсы и внешние сервисы';

export const entityLabel = (entity: Entity) =>
  entity.metadata.title ?? entity.metadata.name;

const categoryOf = (entity: Entity): NodeCategory => {
  switch (entity.kind.toLocaleLowerCase('en-US')) {
    case 'api':
      return 'api';
    case 'resource':
      return entity.spec?.type === 'external-service' ? 'external' : 'resource';
    default:
      return 'component';
  }
};

const systemRefOf = (entity: Entity): string | undefined => {
  const system = entity.spec?.system;
  if (typeof system !== 'string' || !system) return undefined;
  return stringifyEntityRef(
    parseEntityRef(system, {
      defaultKind: 'system',
      defaultNamespace: entity.metadata.namespace,
    }),
  );
};

const EDGE_RELATIONS: Record<string, EdgeCategory> = {
  [RELATION_DEPENDS_ON]: 'dependsOn',
  [RELATION_PROVIDES_API]: 'providesApi',
  [RELATION_CONSUMES_API]: 'consumesApi',
};

// Строит граф из сущностей каталога: системы становятся группами, компоненты, API и ресурсы —
// узлами внутри своей системы; ресурсы без системы попадают в общую группу. Связи берутся из
// relations (dependsOn, providesApi, consumesApi), висящие ссылки на неописанные сущности отбрасываются.
export function buildGraph(entities: Entity[]): ArchGraph {
  const systems = new Map<string, Entity>();
  for (const entity of entities) {
    if (entity.kind.toLocaleLowerCase('en-US') === 'system') {
      systems.set(stringifyEntityRef(entity), entity);
    }
  }

  const nodes: ArchNode[] = [];
  for (const entity of entities) {
    if (entity.kind.toLocaleLowerCase('en-US') === 'system') continue;
    const systemRef = systemRefOf(entity);
    nodes.push({
      id: stringifyEntityRef(entity),
      entity,
      category: categoryOf(entity),
      label: entityLabel(entity),
      subtitle: String(entity.spec?.type ?? entity.kind),
      groupId: systemRef && systems.has(systemRef) ? systemRef : SHARED_GROUP_ID,
    });
  }

  const nodeIds = new Set(nodes.map(n => n.id));
  const edges: ArchEdge[] = [];
  const seen = new Set<string>();
  for (const node of nodes) {
    for (const relation of node.entity.relations ?? []) {
      const category = EDGE_RELATIONS[relation.type];
      if (!category || !nodeIds.has(relation.targetRef)) continue;
      const id = `${category}:${node.id}->${relation.targetRef}`;
      if (seen.has(id)) continue;
      seen.add(id);
      edges.push({ id, source: node.id, target: relation.targetRef, category });
    }
  }

  const groups: ArchGroup[] = [...systems.entries()]
    .map(([id, entity]) => ({ id, label: entityLabel(entity), entity }))
    .sort((a, b) => a.label.localeCompare(b.label));
  if (nodes.some(n => n.groupId === SHARED_GROUP_ID)) {
    groups.push({ id: SHARED_GROUP_ID, label: SHARED_GROUP_LABEL });
  }

  return { nodes, edges, groups };
}

export type GraphFilter = {
  categories: Set<NodeCategory>;
  edgeCategories: Set<EdgeCategory>;
  groups: Set<string>;
};

// Оставляет узлы выбранных категорий и групп и связи между ними; пустые группы скрываются.
export function filterGraph(graph: ArchGraph, filter: GraphFilter): ArchGraph {
  const nodes = graph.nodes.filter(
    n => filter.categories.has(n.category) && filter.groups.has(n.groupId),
  );
  const ids = new Set(nodes.map(n => n.id));
  const edges = graph.edges.filter(
    e =>
      filter.edgeCategories.has(e.category) &&
      ids.has(e.source) &&
      ids.has(e.target),
  );
  const usedGroups = new Set(nodes.map(n => n.groupId));
  return { nodes, edges, groups: graph.groups.filter(g => usedGroups.has(g.id)) };
}
